/// <amd-module name="Repos/Artifact/Discussion/ArtifactDiscussionIntegrationService" />
import * as React from "react";
import { IChange } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    IChangesTreeDiscussionIntegration,
    IDiffMonacoDiscussionIntegration,
    IDiffSummaryDiscussionIntegration,
    IDiscussionIntegrationService,
    IDiscussionThreadCommonProps,
    ILineInfo,
    LineInfoType,
    SpecialCommentPositions
} from "Repos/Discussion/Common/DiscussionContracts";
import { DiscussionThreadPermissions, IDiscussionMarkdownEditorProps } from "Repos/Discussion/DiscussionThread.Types";
import { IDiscussionIntegrationContext, IDiscussionIntegrationProps } from "Repos/Discussion/Editor/Integrations/BaseDiscussionIntegration";
import { ChangesTreeDiscussionIntegration } from "Repos/Discussion/Editor/Integrations/ChangesTreeDiscussionIntegration";
import { DiffSummaryDiscussionIntegration } from "Repos/Discussion/Editor/Integrations/DiffSummaryDiscussionIntegration";
import { MonacoDiscussionIntegration } from "Repos/Discussion/Editor/Integrations/MonacoDiscussionIntegration";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, VssService } from "VSS/Platform/Context";
import { ITreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { IArtifactDiscussionThreadModel } from "./ArtifactDiscussionContracts";
import { IArtifactDiscussionThreadService } from "./ArtifactDiscussionThreadService";
import { filePathEquals, getFilePath, isOriginalSide, newArtifactThreadModel } from "./ArtifactDiscussionUtil";
import { ArtifactDiscussion } from "./Components/ArtifactDiscussion";
import { IEditorSelection } from "Repos/Common/Contracts/Editor";

export interface IArtifactDiscussionIntegrationService extends IDiscussionIntegrationService, IVssService {}

export abstract class ArtifactDiscussionIntegrationService extends VssService implements IArtifactDiscussionIntegrationService {
    public getMonacoIntegration(
        filePath: string,
        diffOrientation: IReadonlyObservableValue<DiffViewerOrientation>,
        getCodeSelectionLinkUrl: (selection: IEditorSelection, editorType?: number, diffOrientation?: DiffViewerOrientation) => string
    ): IDiffMonacoDiscussionIntegration {
        return new MonacoDiscussionIntegration({...this.getIntegrationProps(filePath, diffOrientation), getCodeSelectionLinkUrl});
    }

    public getDiffSummaryIntegrations(
        filePaths: string[],
        diffOrientation: IReadonlyObservableValue<DiffViewerOrientation>
    ): IDiffSummaryDiscussionIntegration[] {
        return filePaths.map(
            filePath => new DiffSummaryDiscussionIntegration({ ...this.getIntegrationProps(filePath, diffOrientation), isRoot: false })
        );
    }

    public getChangesTreeIntegration(fileItemsProvider: ITreeItemProvider<IChange>): IChangesTreeDiscussionIntegration<ITreeItemProvider<IChange>> {
        return new ChangesTreeDiscussionIntegration({ ...this.getIntegrationProps("/"), fileItemsProvider });
    }

    public getRootDiffSummaryIntegration(): IDiffSummaryDiscussionIntegration | undefined {
        return new DiffSummaryDiscussionIntegration({ ...this.getIntegrationProps(""), isRoot: true });
    }

    protected abstract getDiscussionStore(pageContext: IVssPageContext): IArtifactDiscussionThreadService;
    protected abstract getMarkdownEditorProps(): IDiscussionMarkdownEditorProps;

    private filterThreads = (threads: IArtifactDiscussionThreadModel[], filePath: string): IArtifactDiscussionThreadModel[] => {
        return threads.filter(tm => filePathEquals(tm, filePath));
    };

    private getIntegrationContext(threadModel: IArtifactDiscussionThreadModel): IDiscussionIntegrationContext {
        const { threadContext } = threadModel.thread;

        let addToOriginalDiff = false;
        const lineInfo: ILineInfo = {
            lineStart: SpecialCommentPositions.File,
            lineStartOffset: 0,
            lineEnd: SpecialCommentPositions.File,
            lineEndOffset: 0,
            type: LineInfoType.Actual
        };

        if (threadContext) {
            if (threadContext.leftFileEnd && threadContext.leftFileStart) {
                addToOriginalDiff = true;
                lineInfo.lineEnd = threadContext.leftFileEnd.line;
                lineInfo.lineEndOffset = threadContext.leftFileEnd.offset;
                lineInfo.lineStart = threadContext.leftFileStart.line;
                lineInfo.lineStartOffset = threadContext.leftFileStart.offset;
            }
            if (threadContext.rightFileEnd && threadContext.rightFileStart) {
                lineInfo.lineEnd = threadContext.rightFileEnd.line;
                lineInfo.lineEndOffset = threadContext.rightFileEnd.offset;
                lineInfo.lineStart = threadContext.rightFileStart.line;
                lineInfo.lineStartOffset = threadContext.rightFileStart.offset;
            }
        }

        return { addToOriginalDiff, lineInfo };
    }

    private getIntegrationProps(filePath: string, diffOrientation?: IReadonlyObservableValue<DiffViewerOrientation>): IDiscussionIntegrationProps {
        const { pageContext } = this;
        const discussionStore = this.getDiscussionStore(pageContext);
        const discussionThreadContext = discussionStore.getThreadContext();

        return {
            currentUser: discussionThreadContext.currentUser,
            canAddComments: (discussionThreadContext.permissions & DiscussionThreadPermissions.ManageComment) > 0,
            createThreadModel: newArtifactThreadModel,
            diffOrientation,
            discussionStore,
            extractFilePath: getFilePath,
            filePath,
            filterThreads: this.filterThreads,
            getIntegrationContext: this.getIntegrationContext,
            getLineNumber: this.getLineNumber,
            initializeThread: this.initializeThread,
            isOriginalSide,
            pageContext,
            renderDiscussion: (props: IDiscussionThreadCommonProps) => (
                <ArtifactDiscussion
                    artifactUri={discussionThreadContext.artifactUri}
                    author={discussionThreadContext.currentUser}
                    className={props.className}
                    getMarkdownEditorProps={this.getMarkdownEditorProps}
                    onCancelThread={props.onCancelThread}
                    onChangeStatus={props.onChangeStatus}
                    onDeleteThread={props.onDeleteThread}
                    permissions={discussionThreadContext.permissions}
                    personaProvider={discussionThreadContext.personaProvider}
                    thread={props.thread as IArtifactDiscussionThreadModel}
                />
            )
        };
    }

    private getLineNumber = (threadModel: IArtifactDiscussionThreadModel): number | undefined => {
        if (threadModel.thread.threadContext) {
            if (threadModel.thread.threadContext.leftFileEnd) {
                return threadModel.thread.threadContext.leftFileEnd.line;
            }

            if (threadModel.thread.threadContext.rightFileEnd) {
                return threadModel.thread.threadContext.rightFileEnd.line;
            }
        }
    };

    private initializeThread = (
        threadModel: IArtifactDiscussionThreadModel,
        filePath: string,
        integrationContext: IDiscussionIntegrationContext
    ): void => {
        const { addToOriginalDiff, lineInfo } = integrationContext;
        const thread = threadModel.thread;
        thread.threadContext = { filePath } as CommentThreadContext;
        if (addToOriginalDiff) {
            thread.threadContext.leftFileEnd = { line: lineInfo ? lineInfo.lineEnd : 0, offset: lineInfo ? lineInfo.lineEndOffset : 0 };
            thread.threadContext.leftFileStart = { line: lineInfo ? lineInfo.lineStart : 0, offset: lineInfo ? lineInfo.lineStartOffset : 0 };
        } else {
            thread.threadContext.rightFileEnd = { line: lineInfo ? lineInfo.lineEnd : 0, offset: lineInfo ? lineInfo.lineEndOffset : 0 };
            thread.threadContext.rightFileStart = { line: lineInfo ? lineInfo.lineStart : 0, offset: lineInfo ? lineInfo.lineStartOffset : 0 };
        }
    };
}
